import React, { useState, useEffect } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Button } from '../../../shared/components/Button';
import { DocumentMetadata } from '../../../core/ast/document.types';
import { UNIVERSITY_CONFIG } from '../../../config/university.config';
import { BookOpen, User, Calendar, Layers, ShieldCheck } from 'lucide-react';

interface ReportMetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: DocumentMetadata;
  onSave: (updates: Partial<DocumentMetadata>) => void;
}

export const ReportMetadataModal: React.FC<ReportMetadataModalProps> = ({
  isOpen,
  onClose,
  metadata,
  onSave,
}) => {
  const [formData, setFormData] = useState<DocumentMetadata>(metadata);

  useEffect(() => {
    setFormData(metadata);
  }, [metadata, isOpen]);

  const handleApply = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Lab Report Information & Academic Variables"
      maxWidth="620px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Save Changes
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>
          Values defined here populate cover pages, header titles, and report variables throughout your document.
        </p>

        {/* Experiment Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <BookOpen size={13} /> Experiment Identification
          </span>
          <div className="form-group">
            <label className="form-label">Experiment / Report Title</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Diode Characteristic Curve & Active Filter Analysis"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Course Code</label>
              <input
                type="text"
                className="form-input"
                value={formData.courseCode || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, courseCode: e.target.value }))}
                placeholder="e.g. CSE 209 / EEE 303"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Course Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.courseTitle || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, courseTitle: e.target.value }))}
                placeholder="e.g. Electrical Circuits Laboratory"
              />
            </div>
          </div>
        </div>

        {/* Student & Course Section Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <User size={13} /> Student & Section Details
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Student Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.author.studentName || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    author: { ...prev.author, studentName: e.target.value },
                  }))
                }
                placeholder="e.g. Aftab Hossain"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Student ID</label>
              <input
                type="text"
                className="form-input"
                value={formData.author.studentId || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    author: { ...prev.author, studentId: e.target.value },
                  }))
                }
                placeholder="e.g. 2021-1-60-000"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Section / Group</label>
              <input
                type="text"
                className="form-input"
                value={formData.author.sectionOrGroup || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    author: { ...prev.author, sectionOrGroup: e.target.value },
                  }))
                }
                placeholder="e.g. Section 02, Group A"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Student Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.author.email || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    author: { ...prev.author, email: e.target.value },
                  }))
                }
                placeholder="e.g. student@ewubd.edu"
              />
            </div>
          </div>
        </div>

        {/* Faculty and Department */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <ShieldCheck size={13} /> Faculty & Department
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-2)' }}>
            <div className="form-group">
              <label className="form-label">Faculty / Instructor Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.instructor.instructorName || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    instructor: { ...prev.instructor, instructorName: e.target.value },
                  }))
                }
                placeholder="e.g. Dr. Mohammad Rashed"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Designation</label>
              <input
                type="text"
                className="form-input"
                value={formData.instructor.designation || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    instructor: { ...prev.instructor, designation: e.target.value },
                  }))
                }
                placeholder="e.g. Associate Professor"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Department</label>
            <select
              className="form-select"
              value={formData.institution.department || UNIVERSITY_CONFIG.defaultDepartment}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  institution: { ...prev.institution, department: e.target.value },
                  instructor: { ...prev.instructor, department: e.target.value },
                }))
              }
            >
              {UNIVERSITY_CONFIG.departments.map((deptName) => (
                <option key={deptName} value={deptName}>
                  {deptName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Dates & Institution */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={12} /> Submission Date
            </label>
            <input
              type="date"
              className="form-input"
              value={formData.submissionDate || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, submissionDate: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Layers size={12} /> University / Institution
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.institution.name || UNIVERSITY_CONFIG.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  institution: { ...prev.institution, name: e.target.value },
                }))
              }
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
